/**
 * SectorMap — read-only hall map showing sectors as clickable boxes.
 * Used in the purchase flow when a hall has multiple sectors.
 * Props:
 *   sectors   — array of sector objects with { id, name, name_en, x, y, width, height, rotation }
 *   seats     — full seat array (from API), used to compute availability per sector
 *   soldSeatIds  — array of sold seat IDs
 *   lockedSeatIds — array of locked seat IDs
 *   onSectorSelect(sector) — called when user clicks a sector
 */
export default function SectorMap({ sectors = [], seats = [], soldSeatIds = [], lockedSeatIds = [], onSectorSelect }) {

    const getSectorStats = (sectorId) => {
        const sectorSeats = seats.filter(s => s.sector_id === sectorId)
        const total = sectorSeats.length
        const sold = sectorSeats.filter(s => soldSeatIds.includes(s.id)).length
        const locked = sectorSeats.filter(s => lockedSeatIds.includes(s.id) && !soldSeatIds.includes(s.id)).length
        const available = total - sold - locked
        return { total, available, sold, locked }
    }

    const lang = localStorage.getItem('app_lang') || 'bg'

    return (
        <div>
            <p className="text-gray-400 text-sm mb-4 text-center">
                {window.t('izberete_sektor_za_da_vidite_nalichnite_mesta')}
            </p>

            <div className="overflow-auto">
                <div
                    className="relative bg-gray-950 border border-gray-800 rounded-xl mx-auto"
                    style={{ width: '900px', height: '600px' }}
                >
                    {/* Stage indicator */}
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 w-2/3 h-10 border-2 border-dashed border-gray-700 rounded-t-[100px] flex items-center justify-center text-gray-500 font-bold uppercase tracking-widest text-xs pointer-events-none">
                        {window.t('scena_ekran')}
                    </div>

                    {sectors.map(sector => {
                        const stats = getSectorStats(sector.id)
                        const isSoldOut = stats.available === 0 && stats.total > 0
                        const name = lang === 'en' ? (sector.name_en || sector.name) : sector.name

                        return (
                            <button
                                key={sector.id}
                                onClick={() => !isSoldOut && onSectorSelect(sector)}
                                disabled={isSoldOut}
                                className={`absolute border-2 rounded-lg flex flex-col items-center justify-center transition-all ${isSoldOut
                                    ? 'border-gray-700 opacity-40 cursor-not-allowed'
                                    : 'border-primary-500/70 hover:border-primary-400 hover:scale-[1.02] cursor-pointer'
                                    }`}
                                style={{
                                    left: (sector.x || 20) + 'px',
                                    top: (sector.y || 60) + 'px',
                                    width: (sector.width || 200) + 'px',
                                    height: (sector.height || 120) + 'px',
                                    transform: sector.rotation ? `rotate(${sector.rotation}deg)` : undefined,
                                    background: isSoldOut ? 'rgba(75,85,99,0.15)' : 'rgba(139,92,246,0.12)',
                                }}
                                title={isSoldOut ? `${name} — ${window.t('razprodadeno')}` : `${name} — ${stats.available} ${window.t('svobodni')}`}
                            >
                                <p className="text-white font-bold text-sm pointer-events-none">{name}</p>
                                {stats.total > 0 ? (
                                    <p className={`text-xs pointer-events-none mt-1 ${isSoldOut ? 'text-gray-500' : 'text-primary-400'}`}>
                                        {isSoldOut ? `${window.t('razprodadeno')}` : `${stats.available} ${window.t('svobodni')}`}
                                    </p>
                                ) : null}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

